import {
  IconButton,
  TextInput,
  ColoredNextIcon as coloredNextIcon,
  ColoredPreviousIcon as coloredPreviousIcon,
  NextIcon as nextIcon,
  PreviousIcon as previousIcon,
} from '@/shared/ui';
import useBookmarkPaginationContainer from './useBookmarkPaginationContainer';

export type BookmarkPaginationContainerProps = {
  isDisabled?: boolean;
};

/**
 * Renders page navigation for the ECG bookmark strip list. Renders an empty container until useBookmarkPaginationContainer reports pagination should be shown, then wires the page input and the previous and next buttons, disabling them at the ends of the range or when the parent passes isDisabled.
 *
 * @param props - Pagination inputs including the isDisabled flag applied to every control.
 * @returns The bookmark pagination controls, or an empty container when pagination is hidden.
 */
const BookmarkPaginationContainer = ({ isDisabled }: BookmarkPaginationContainerProps) => {
  const {
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    pageString,
    inputSize,
    isError,
    maxPageNumber,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
  } = useBookmarkPaginationContainer({ isDisabled });

  return (
    <div className="pagination-container">
      {isShowPagination && (
        <>
          <IconButton
            id="bookmarks-previous-page-btn"
            isFilled
            disabled={isDisabledPrev || isDisabled}
            iconComponent={
              <img src={isDisabledPrev ? previousIcon : coloredPreviousIcon} alt="Previous icon" />
            }
            onClick={onClickPrevious}
          />
          <TextInput
            id="bookmarks-page-input"
            ariaLabel="Page number"
            suffix={`/ ${maxPageNumber}`}
            name="pageString"
            value={pageString}
            onChange={onChangePageString}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            isNumberFormat
            shouldChangeSize
            className="page-input"
            size={inputSize}
            isError={isError}
            disabled={isDisabled}
            decimalSeparator={false}
          />
          <IconButton
            id="bookmarks-next-page-btn"
            isFilled
            disabled={isDisabledNext || isDisabled}
            iconComponent={
              <img src={isDisabledNext ? nextIcon : coloredNextIcon} alt="Next icon" />
            }
            onClick={onClickNext}
          />
        </>
      )}
    </div>
  );
};

export default BookmarkPaginationContainer;
